import { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_HOTEL } from 'constants/offer'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import getLPCRegionLOS from 'lib/offer/getLPCRegionLOS'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import BodyText from 'LuxKit/Typography/BodyText'
import PriceRowPriceCaptionLoadingSkeleton from 'LuxKit/PricePoints/PriceRowPriceCaptionLoadingSkeleton'
import PriceRowLuxPlusPriceLoadingSkeleton from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPriceLoadingSkeleton'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import { useOfferPackagePrice } from 'hooks/Offers/useOfferPriceNew'
import { useSearchPrices } from 'hooks/Search/useSearchPrices'
import getOfferDurationText from 'lib/offer/getOfferDurationText'
import useOffersBestPrices from 'hooks/useOfferBestPrices'
import { buildSearchParamsKey } from 'lib/search/searchUtils'
import { EmptyObject } from 'lib/object/objectUtils'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { useAppSelector } from 'hooks/reduxHooks'
import { isHotelPackage } from 'lib/offer/offerTypes'
import type { LEOfferPricingComponentProps } from 'components/OfferList/OfferCards/LEOfferPriceDetails'

function ChatHotelCarouselPrice(props: LEOfferPricingComponentProps) {
  const {
    offer,
    selectedDatesAndRoomsFilter = EmptyObject,
  } = props
  const intl = useIntl()
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const bestPkg = useOffersBestPrices(offer, selectedDatesAndRoomsFilter)
  const pkg = bestPkg?.bestPricePackage ?? offer.lowestPricePackage!

  // For a dated search the live availability rate carries the correct fee/region price,
  // while the raw package's static price is 0 for Always-On offers (rendering "A$0"). When a
  // dated rate is loaded, price from it via the same hook the offer page + search tiles use
  // (useSearchPrices) so this card matches them. No-date renders keep the package pricing.
  const bestPrices = useAppSelector((state) => state.offer.offerBestPrices[offer.id])
  const searchKey = useMemo(
    () => buildSearchParamsKey(selectedDatesAndRoomsFilter.checkIn, selectedDatesAndRoomsFilter.checkOut, selectedDatesAndRoomsFilter.rooms),
    [selectedDatesAndRoomsFilter],
  )
  const datedBestPrice = bestPrices?.[searchKey]
  const datedRate = datedBestPrice?.available ? datedBestPrice.rate : undefined
  // While a dated rate is still being fetched we have no correct price yet: the raw package
  // falls back to lowestPricePackage (0 for Always-On, or a stale no-date lead for flash).
  // Show a skeleton instead of that wrong price. No-date renders never fetch, so loading is
  // falsy and this gate leaves homepage/wishlist untouched.
  const isLoadingDatedPrice = !!bestPkg?.loading && !datedRate
  // A dated lookup that has resolved without an available rate (sold out / errored /
  // explicitly unavailable) has no correct price — show an unavailable label rather than
  // fall through to the package price (0 for Always-On, which renders "A$0").
  const isDatedLookupUnavailable = !datedRate && !bestPkg?.loading &&
    ((datedBestPrice != null && !datedBestPrice.available) || !!bestPkg?.soldOut || !!bestPkg?.error)

  // Always-On/SPP prices are date-dependent — the offer page and search/drawer withhold the
  // price when no dates are selected rather than show the static package price as a "from"
  // lead. Flash hotels have a legit no-date "from" lead and are unaffected by this gate.
  const hasDates = !!(selectedDatesAndRoomsFilter.checkIn && selectedDatesAndRoomsFilter.checkOut)
  const isAlwaysOnWithoutDates = offer.type === OFFER_TYPE_ALWAYS_ON && !hasDates

  const packagePricing = useOfferPackagePrice(pkg, offer)
  const searchPricing = useSearchPrices({ offer, pkg, rate: datedRate, filters: selectedDatesAndRoomsFilter })

  const defaultPricing = datedRate ? searchPricing.defaultPricing : packagePricing.default
  const memberPricing = datedRate ? searchPricing.memberPricing : packagePricing.member
  const { saleUnit, price, value, discountPercent } = defaultPricing

  const valueToDisplay = canViewLuxPlusBenefits && memberPricing?.value ? memberPricing.value : value
  const discountToDisplay = canViewLuxPlusBenefits && memberPricing ? memberPricing.discountPercent : discountPercent

  const shouldShowRate = !offer.isDiscountPillHidden && discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE

  const shouldShowValue = pkg.value > 0 && price && value > price && pkg.shouldDisplayValue &&
    (offer.type == OFFER_TYPE_HOTEL || (isHotelPackage(pkg) && !pkg.roomRate?.inclusionsHideValue))

  let staysFrom = offer.minDuration
  const LPCRegionLOS = getLPCRegionLOS(offer)
  if (LPCRegionLOS && offer.packages.some(p => p.duration === LPCRegionLOS)) {
    staysFrom = LPCRegionLOS
  }
  const showStaysFromLabel = staysFrom && offer.type === OFFER_TYPE_ALWAYS_ON && LPCRegionLOS && staysFrom < pkg.duration
  const durationText = useMemo(() => getOfferDurationText(intl, offer, pkg), [intl, offer, pkg])

  if (isLoadingDatedPrice) {
    return <div data-testid="offer-pricing-skeleton">
      <PriceRowPriceCaptionLoadingSkeleton width="8ch" />
      <PriceRowLuxPlusPriceLoadingSkeleton size="L" />
    </div>
  }

  if (isDatedLookupUnavailable) {
    return <BodyText variant="large" weight="bold">
      <FormattedMessage
        id="2vAx_-AMvDvxXp2q_ChZ"
        defaultMessage="Price unavailable"
        description="Label shown when planned item hotel pricing is unavailable"
      />
    </BodyText>
  }

  if (isAlwaysOnWithoutDates) {
    return null
  }

  return (<>
    <PriceRowPriceCaption>{durationText}</PriceRowPriceCaption>
    <LuxPlusPriceStack
      price={price}
      memberPrice={memberPricing?.price}
      size="L"
      saleUnit={saleUnit}
      offerConfig={offer.luxPlus}
      testid="main-offer-pricing"
    />
    {shouldShowValue && <PriceRowValueDiscountWithCaption
      size="L"
      originalValue={Math.ceil(valueToDisplay)}
      discountPercentage={shouldShowRate ? discountToDisplay : undefined}
      data-testid="le-offer-pricing"
    />}
    {staysFrom && showStaysFromLabel && <PriceRowCaption>
      <FormattedMessage
        id="7PFPr3tSopW8Hgmmb_Dy"
        defaultMessage="Stays from {staysFrom, plural, one {# night} other {# nights}} available"
        description="Caption indicating minimum stay duration available for an always-on offer"
        values={{ staysFrom }}
      />
    </PriceRowCaption>}
    {!!pkg.roomOccupancy && offer.type === 'tour' && <PriceRowCaption>
      {pkg.roomOccupancy}
    </PriceRowCaption>}
  </>)
}

export default ChatHotelCarouselPrice
